import {Accordion, createStyles, Group} from '@mantine/core';
import React, {useState} from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

const COLLAPSIBLE_SECTION_VALUE = 'content';
const DATA_TEST_COMPONENT = 'CollapsibleTransitionCard';

interface CollapsibleTransitionCardContentProps {
  header: React.ReactNode;
  headerAdornment?: React.ReactNode;
  alwaysVisibleContent?: React.ReactNode;
  alwaysVisibleContentClassName: string;
  children: React.ReactNode;
}

function CollapsibleTransitionCardContent({
  header,
  headerAdornment,
  alwaysVisibleContent,
  alwaysVisibleContentClassName,
  children,
}: CollapsibleTransitionCardContentProps): JSX.Element {
  return (
    <Accordion.Item value={COLLAPSIBLE_SECTION_VALUE}>
      <Accordion.Control {...getTestProps({component: DATA_TEST_COMPONENT, element: 'toggleButton'})}>
        <Group spacing={8} noWrap={true}>
          <span>{header}</span>
          {headerAdornment != null && <Group spacing={4}>{headerAdornment}</Group>}
        </Group>
      </Accordion.Control>
      {alwaysVisibleContent != null && (
        <div className={alwaysVisibleContentClassName}>{alwaysVisibleContent}</div>
      )}
      <Accordion.Panel>
        <div {...getTestProps({component: DATA_TEST_COMPONENT, element: 'contents'})}>{children}</div>
      </Accordion.Panel>
    </Accordion.Item>
  );
}

export interface CollapsibleTransitionCardProps {
  /**
   * The header label.
   */
  header: React.ReactNode;
  /**
   * Optional inline content rendered after the header label (e.g. chips, badges).
   */
  headerAdornment?: React.ReactNode;
  /**
   * Content rendered between the header and collapsible children, always visible regardless of
   * open/closed state.
   */
  alwaysVisibleContent?: React.ReactNode;
  children: React.ReactNode;
  dataTestAttrs?: DataTestAttrs;
  isOpenByDefault?: boolean;
  /**
   * When true, the collapsible children (panel content) span the full width of the card with no
   * horizontal padding, e.g. for tables that should extend edge-to-edge.
   */
  fullWidthChildren?: boolean;
}

export default function CollapsibleTransitionCard({
  header,
  headerAdornment,
  alwaysVisibleContent,
  children,
  isOpenByDefault = false,
  fullWidthChildren = false,
  dataTestAttrs = {component: DATA_TEST_COMPONENT, element: 'root'},
}: CollapsibleTransitionCardProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(isOpenByDefault);
  const {classes} = useStyles();

  return (
    <div className={classes.wrapper} {...getTestProps(dataTestAttrs)}>
      <Accordion
        variant="default"
        radius="md"
        value={isOpen ? COLLAPSIBLE_SECTION_VALUE : null}
        onChange={(value) => {
          setIsOpen(value === COLLAPSIBLE_SECTION_VALUE);
        }}
        transitionDuration={200}
        chevronPosition="left"
        chevron={<BenchlingIcon icon={isOpen ? 'chevron-down' : 'chevron-right'} />}
        disableChevronRotation={true}
        classNames={{
          control: classes.accordionControl,
          ...(fullWidthChildren && {
            content: classes.accordionContentFullWidth,
            panel: classes.panelContentFullWidth,
          }),
        }}
      >
        <CollapsibleTransitionCardContent
          header={header}
          headerAdornment={headerAdornment}
          alwaysVisibleContent={alwaysVisibleContent}
          alwaysVisibleContentClassName={classes.alwaysVisibleContent}
        >
          {children}
        </CollapsibleTransitionCardContent>
      </Accordion>
    </div>
  );
}

const useStyles = createStyles((theme) => ({
  wrapper: {
    marginBottom: theme.spacing.sm,
    border: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: theme.radius.md,
  },
  accordionControl: {
    // Increases padding to the left of the accordion's chevron
    paddingLeft: theme.spacing.md,
    // Matches wrapper, so accordion control doesn't cut off the border
    borderRadius: theme.radius.md,
    // Adds spacing between the accordion control and its content
    marginBottom: theme.spacing.sm,
  },
  // Allows accordion content to span the full width of the card
  accordionContentFullWidth: {
    padding: 0,
    width: '100%',
  },
  alwaysVisibleContent: {
    marginLeft: theme.spacing.md,
    paddingBottom: theme.spacing.xs,
  },
  panelContentFullWidth: {
    marginLeft: -theme.spacing.md,
    marginRight: -theme.spacing.md,
    width: `calc(100% + ${Number(theme.spacing.md) * 2}px)`,
  },
}));
